import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { PasteExerciseComponent } from "../../../../parts/PasteExerciseComponent";
import SvgIcon from "../../../../parts/SvgIcon";

export const AddExerciseProgram = ({
  session,
  sessionIndex,
  activeUserToHandleCopyPasteAction,
  program,
  cycle,
  exercisesInClipboard,
  date,
  programOrder,
  sectionAthleteId,
  organizationCoach,
}) => {
  const [exercisesToCopy, setExercisesToCopy] = useState(null);

  /**
   * Check if there is an exercise storage and ready to be pasted
   */

  useEffect(() => {
    if (typeof window !== "undefined") {
      const exerciseInLocalStorage = localStorage.getItem("exercisesToCopy");
      setExercisesToCopy(
        exerciseInLocalStorage ? JSON.parse(exerciseInLocalStorage) : null
      );
    }
  }, [exercisesInClipboard]);
  const notSingleWorkout = cycle?.storeType !== "single-workout";

  return exercisesToCopy ? (
    <div className="add-paste-exercise">
      <AddExerciseLayout session={session} sessionIndex={sessionIndex} />
      <PasteExerciseComponent
        athlete={activeUserToHandleCopyPasteAction}
        date={date}
        withFullInfo={true}
        classNames={"flex flex-col items-center gap-2 p-2"}
        isProgram={true}
        sessionProgram={session}
        programNumber={program}
        cycle={cycle}
        query={exercisesInClipboard}
      />
      {notSingleWorkout && (
        <ImportWorkoutLayout session={session} sessionIndex={sessionIndex} />
      )}
      <AddWorkoutSectionLayout
        session={session}
        programOrder={programOrder}
        cycleId={cycle?._id}
        sectionAthleteId={sectionAthleteId}
        organizationCoach={organizationCoach}
      />
    </div>
  ) : (
    <div className="add-paste-exercise">
      <AddExerciseLayout session={session} sessionIndex={sessionIndex} />
      {notSingleWorkout && (
        <ImportWorkoutLayout session={session} sessionIndex={sessionIndex} />
      )}
      <AddWorkoutSectionLayout
        session={session}
        programOrder={programOrder}
        cycleId={cycle?._id}
        sectionAthleteId={sectionAthleteId}
        organizationCoach={organizationCoach}
      />
    </div>
  );
};

AddExerciseProgram.propTypes = {
  session: PropTypes.object,
  sessionIndex: PropTypes.number,
  activeUserToHandleCopyPasteAction: PropTypes.object,
  program: PropTypes.number,
  cycle: PropTypes.object,
  exercisesInClipboard: PropTypes.string,
  date: PropTypes.string,
  programOrder: PropTypes.number,
  sectionAthleteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  organizationCoach: PropTypes.string,
};

const AddExerciseLayout = ({ session, sessionIndex }) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <a
      className="add-workout"
      href={`/actions/refresh?modal[type]=add-exercise&programWeek=${
        session.week
      }&programSession=${sessionIndex + 1}&editMode=false`}
    >
      <SvgIcon
        src="/img/new-icons/add-square-outline.svg"
        className="h-11 w-11"
      />
      <p className="p4 text-center">{t("programs.addExercise")}</p>
    </a>
  );
};

AddExerciseLayout.propTypes = {
  session: PropTypes.object,
  sessionIndex: PropTypes.number,
};

const ImportWorkoutLayout = ({ session, sessionIndex }) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <a
      className="add-workout"
      href={`/actions/refresh?modal[type]=import-workout&programWeek=${
        session.week
      }&programSession=${sessionIndex + 1}&exercisesLength=0&highestOrder=0`}
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-solid border-(--white-20)">
        <SvgIcon src="/img/new-icons/import.svg" className="h-6 w-6" />
      </span>
      <p className="p4 text-center">{t("programs.importWorkout")}</p>
    </a>
  );
};

ImportWorkoutLayout.propTypes = {
  session: PropTypes.object,
  sessionIndex: PropTypes.number,
};

// Adds a workout section (text separator) at the start of an empty program
// session. Mirrors the calendar day editor's "Add Workout Section" action, but
// scoped to the program session (cycle/program/week/session) rather than a date.
const AddWorkoutSectionLayout = ({
  session,
  programOrder,
  cycleId,
  sectionAthleteId,
  organizationCoach,
}) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <a
      className="add-workout"
      href="#_"
      onClick={(e) => {
        e.preventDefault();
        let createQuery = `?proxyAthlete=${
          sectionAthleteId || ""
        }&reqSource=program`;
        if (organizationCoach) {
          createQuery += `&organizationCoach=${organizationCoach}`;
        }
        submit(`/actions/exercise-section/create${createQuery}`, {
          method: "POST",
          body: {
            name: t("exerciseEditor.newSection"),
            // First section of an empty session. -0.5 matches the shared
            // getHighestOrder([], []) + 0.5 convention, so the first exercise
            // added afterwards (order 0) falls under it.
            order: -0.5,
            cycleId,
            program: programOrder,
            programWeek: session.week,
            programSession: session.order,
          },
        });
      }}
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-solid border-(--white-20)">
        <SvgIcon
          src="/img/new-icons/workout-sections-icon.svg"
          classNames="h-6 w-6"
        />
      </span>
      <p className="p4 text-center">{t("programs.addWorkoutSection")}</p>
    </a>
  );
};

AddWorkoutSectionLayout.propTypes = {
  session: PropTypes.object,
  programOrder: PropTypes.number,
  cycleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sectionAthleteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  organizationCoach: PropTypes.string,
};
